import axios, { AxiosError, AxiosResponse } from 'axios'
import { config } from '@/config'
import { formatResponse } from './helpers/formatResponse'
import type { FlowStateName } from '@/types/DashboardV1.types'

/**
 * DashboardFlowTransitionAPI — FSM state-sync endpoint client.
 *
 * Backed by `POST /v1/dashboards/:id/flow-transition`
 * (Slice 1.3 of the Step 5 Deterministic Panels plan,
 * `plans/2026-04-29-step-5-deterministic-panels-and-chat-context.md` §Slice 1.3).
 *
 * The success body is the **raw** response shape (no envelope) — same
 * pattern as DashboardProposedPanels.ts and DashboardFilters.ts.
 * `formatResponse` is only called on the error path.
 *
 * Error 409 signals an FSM violation (INVALID_FSM_TRANSITION).
 * Standard 401/403/500 follow the same Axios error pattern.
 */

export type FlowTransitionState = Extract<
  FlowStateName,
  'awaiting_credential' | 'awaiting_domain' | 'awaiting_filters' | 'awaiting_panels' | 'complete'
>

export interface FlowTransitionTemplateMessage {
  id: string
  role: 'user' | 'assistant'
  kind: 'template'
  content: string
  timestamp: string
}

export interface FlowTransitionResponse {
  flowState: FlowStateName
  flowContext: Record<string, unknown>
  templateMessages: FlowTransitionTemplateMessage[]
}

export const DashboardFlowTransitionAPI = {
  transition: async (
    token: string,
    args: {
      /** UUID of the dashboard being created. */
      dashboardId: string
      /** The FSM state this transition is moving to. */
      toState: FlowTransitionState
      /** Partial flow_context merge sent to the backend. */
      contextDelta: Record<string, unknown>
      /** User-side template text — echoes the deterministic choice. */
      userBubble: string
      /** Assistant-side template narration. */
      assistantBubble: string
      /**
       * Optional idempotency key — 60s TTL on the backend.
       * Generated per click-invocation by the service thunk.
       */
      idempotencyKey?: string
    }
  ): Promise<{
    data: FlowTransitionResponse | undefined
    err: string | undefined
    status: number | undefined
    errorCode: string | undefined
  }> => {
    try {
      const body: {
        toState: FlowTransitionState
        contextDelta: Record<string, unknown>
        userBubble: string
        assistantBubble: string
        idempotencyKey?: string
      } = {
        toState: args.toState,
        contextDelta: args.contextDelta,
        userBubble: args.userBubble,
        assistantBubble: args.assistantBubble,
      }
      if (args.idempotencyKey) {
        body.idempotencyKey = args.idempotencyKey
      }

      const resp: AxiosResponse<FlowTransitionResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/dashboards/${encodeURIComponent(args.dashboardId)}/flow-transition`,
        headers: { Authorization: `Bearer ${token}` },
        data: body,
      })
      // Raw shape — no envelope unwrap. resp.data IS the FlowTransitionResponse.
      return {
        data: resp.data,
        err: undefined,
        status: resp.status,
        errorCode: undefined,
      }
    } catch (error) {
      const axiosErr = error as AxiosError<{ error?: string; reason?: string }>
      const status = axiosErr.response?.status
      const body = axiosErr.response?.data
      const errorCode = body?.error ?? body?.reason
      const formatted = formatResponse<FlowTransitionResponse>(null, axiosErr)
      return {
        data: formatted.data,
        err: formatted.err,
        status,
        errorCode,
      }
    }
  },
}
